import React, { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { ConfigProvider, Spin } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { isAuthenticated, getCurrentUser, getUser } from './services/auth'
import AppLayout from './components/AppLayout'
import './styles/index.css'

// Lazy load pages
const LoginPage = lazy(() => import('./pages/LoginPage'))
const WelcomePage = lazy(() => import('./pages/WelcomePage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))

// Observation
const ObservationHall = lazy(() => import('./pages/ObservationHall'))
const ObserverView = lazy(() => import('./pages/ObserverView'))

// Admin pages
const UserManagement = lazy(() => import('./pages/admin/UserManagement'))
const RoleManagement = lazy(() => import('./pages/admin/RoleManagement'))
const PermissionManagement = lazy(() => import('./pages/admin/PermissionManagement'))
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'))
const SystemConfigPage = lazy(() => import('./pages/admin/SystemConfigPage'))
const DictTablePage = lazy(() => import('./pages/admin/DictTablePage'))

// Process management (未完成 → ComingSoon)
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'))
const ScoreQuery = lazy(() => import('./pages/ScoreQuery'))
const CourseManagement = lazy(() => import('./pages/CourseManagement'))
const IconLibraryPage = lazy(() => import('./pages/IconLibrary'))
const ProcessFlowManagement = lazy(() => import('./pages/ProcessFlowManagement'))

const ExamManagement = lazy(() => import('./pages/ExamManagement'))
const ExamScoring = lazy(() => import('./pages/ExamScoring'))
const ExamStudy = lazy(() => import('./pages/ExamStudy'))

// Learning management
const LearningOverview = lazy(() => import('./pages/LearningOverview'))
const LearningStudy = lazy(() => import('./pages/LearningStudy'))

// Dashboard (existing main page)
const DashboardPage = lazy(() => import('./DashboardPage'))

const EngineManagement = lazy(() => import('./pages/EngineManagement'))

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
)

/** Auth guard for protected routes */
function ProtectedRoute({ children }) {
  const [checking, setChecking] = useState(true)
  const [authed, setAuthed] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const token = isAuthenticated()
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    // Verify token with server
    getCurrentUser().then(user => {
      if (!user) {
        navigate('/login', { replace: true })
        return
      }
      setAuthed(true)
      setChecking(false)
    }).catch(() => {
      navigate('/login', { replace: true })
    })
  }, [])

  if (!authed) return <Loading />
  return children ? children : <Outlet />
}

/** Layout wrapper for pages that need sidebar */
function LayoutPage() {
  return (
    <AppLayout>
      <Suspense fallback={<Loading />}>
        <Outlet />
      </Suspense>
    </AppLayout>
  )
}

/** Role guard — 防止低权限用户手输 URL 进入无权限页面（后端接口仍有二次校验） */
function RoleGuard({ roles, children }) {
  const user = getUser()
  if (!user || !roles.includes(user.role)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#999' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 16 }}>当前角色无权访问此页面</div>
      </div>
    )
  }
  return children
}

export default function RouterApp() {
  return (
    <ConfigProvider locale={zhCN}>
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected routes with sidebar */}
            <Route element={<ProtectedRoute><LayoutPage /></ProtectedRoute>}>
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/" element={<Navigate to="/welcome" replace />} />

              {/* System management（仅 admin） */}
              <Route path="/admin/users" element={<RoleGuard roles={['admin']}><UserManagement /></RoleGuard>} />
              <Route path="/admin/roles" element={<RoleGuard roles={['admin']}><RoleManagement /></RoleGuard>} />
              <Route path="/admin/permissions" element={<RoleGuard roles={['admin']}><PermissionManagement /></RoleGuard>} />
              <Route path="/admin/logs" element={<RoleGuard roles={['admin']}><AuditLogPage /></RoleGuard>} />
              <Route path="/admin/config" element={<RoleGuard roles={['admin']}><SystemConfigPage /></RoleGuard>} />

              {/* Process management（图标库仅 admin；流程管理含工况/仿真 Tab，老师可用） */}
              <Route path="/process/types" element={<RoleGuard roles={['admin', 'process_admin', 'training_teacher']}><ProcessFlowManagement /></RoleGuard>} />
              <Route path="/process/icon-library" element={<RoleGuard roles={['admin']}><IconLibraryPage /></RoleGuard>} />
              <Route path="/process/engine" element={<RoleGuard roles={['admin', 'process_admin']}><EngineManagement /></RoleGuard>} />

              {/* Dictionary management（admin + 主任） */}
              <Route path="/dict/:tableName" element={<RoleGuard roles={['admin', 'process_admin']}><DictTablePage /></RoleGuard>} />

              {/* Training management（admin + 老师） */}
              <Route path="/training/courses" element={<RoleGuard roles={['admin', 'training_teacher']}><CourseManagement /></RoleGuard>} />
              <Route path="/training/exams" element={<RoleGuard roles={['admin', 'training_teacher']}><ExamManagement /></RoleGuard>} />
              <Route path="/training/scoring" element={<RoleGuard roles={['admin', 'training_teacher']}><ExamScoring /></RoleGuard>} />

              {/* Learning management */}
              <Route path="/learning/study" element={<LearningStudy />} />
              <Route path="/learning/exam" element={<ExamStudy />} />
              <Route path="/learning/scores" element={<ScoreQuery />} />
              <Route path="/learning/observation-hall" element={<ObservationHall />} />
            </Route>

            {/* Observation view — full screen without sidebar */}
            <Route element={<ProtectedRoute />}>
              <Route path="/observation/view/:engineId" element={<ObserverView />} />
            </Route>

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ConfigProvider>
  )
}
